import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";

export default function EmailFeedback() {
  const [params] = useSearchParams();
  const activityId = params.get("activity");
  const [wrong, setWrong] = useState("");
  const [fix, setFix] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    getMe().then((me) => { if (me?.email) setEmail(me.email); });
  }, []);

  const submit = async () => {
    if (!wrong.trim() || !activityId) return;
    setBusy(true);
    try {
      await api.post("/email/feedback", {
        activity_id: activityId,
        what_went_wrong: wrong.trim(),
        how_to_make_it_right: fix.trim(),
        contact_email: email || undefined,
      });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-background pb-32" data-testid="email-feedback-page">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <Link to="/" className="btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /></Link>
        <div>
          <div className="eyebrow">Private feedback</div>
          <h1 className="font-display text-3xl mt-1 text-foreground">Tell us what went wrong</h1>
        </div>
      </header>

      <p className="px-5 mt-2 text-sm text-foreground/65 max-w-xl">
        This message goes only to the Habano admin team. It is not shown publicly. We will follow up directly.
      </p>

      {done ? (
        <section className="lux-card mx-5 mt-6 p-8 text-center" data-testid="email-feedback-done">
          <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto" strokeWidth={1.5} />
          <div className="font-display text-2xl text-foreground mt-3">Thank you</div>
          <p className="text-foreground/65 text-sm mt-2 max-w-xs mx-auto">
            We received your message. The admin team will reach out shortly.
          </p>
          <Link to="/" className="mt-5 inline-block btn-outline text-xs" style={{ minHeight: 48 }}>Back to Habano</Link>
        </section>
      ) : (
        <section className="lux-card mx-5 mt-6 p-5 space-y-4">
          {!activityId && (
            <div className="px-3 py-2 border border-amber-500/60 text-amber-300 text-xs flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Open this page from the follow-up email link to link your report to the right activity.
            </div>
          )}
          <label className="block">
            <div className="eyebrow mb-1">What went wrong</div>
            <textarea
              rows={4}
              data-testid="ef-wrong"
              value={wrong}
              onChange={(e) => setWrong(e.target.value)}
              className="form-input"
              style={{ fontSize: 16 }}
              placeholder="Describe what happened…"
            />
          </label>
          <label className="block">
            <div className="eyebrow mb-1">How can we make it right? (optional)</div>
            <textarea
              rows={3}
              data-testid="ef-fix"
              value={fix}
              onChange={(e) => setFix(e.target.value)}
              className="form-input"
              style={{ fontSize: 16 }}
              placeholder="Anything that would help resolve this?"
            />
          </label>
          <label className="block">
            <div className="eyebrow mb-1">Reply to (optional)</div>
            <input
              type="email"
              data-testid="ef-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              style={{ minHeight: 48, fontSize: 16 }}
              placeholder="you@example.com"
            />
          </label>
          <button
            onClick={submit}
            disabled={!wrong.trim() || !activityId || busy}
            className="btn-gold text-sm w-full"
            style={{ minHeight: 48 }}
            data-testid="ef-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to Habano admin
          </button>
        </section>
      )}
    </main>
  );
}
